import { FC, useEffect, useRef } from 'react';
import { IQuestionConfig } from '../core/IQuestion';
import { IGenericQuestionProvider, IQuestionViewProps } from '../core/question-provider/IQuestionProvider';
import { MiniWizardViewModel } from '../core/MiniWizardViewModel';
import { Step } from '../../Step';
import { InternalSelect, ISelected, SearchableSelectField } from '../../../components/SearchableSelectField';
import { isPlatform } from '@ionic/react';
import { SupportGroupDataProvider } from '../../../caremap/SupportGroupDataProvider';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { LabelProvider } from '../../../config/LabelProvider';
import { Text } from '../../../components/Text';
import { CaregiverToggle } from '../../../household/CaregiverToggle';

const questionType = 'household' as const;
type IQuestionType = typeof questionType;

type IRelationshipQuestionProps = {
    recipientFirstName: string;
};

export type IRelationshipQuestionAnswer = {
    relationship?: ISelected;
    primaryCaregiver?: boolean;
};

export type IRelationshipQuestion = IQuestionConfig<IQuestionType, IRelationshipQuestionAnswer, IRelationshipQuestionProps>;

export class RelationshipQuestionProvider implements IGenericQuestionProvider<IRelationshipQuestion> {
    questionType = questionType;

    validate = (question: IRelationshipQuestion) => (question.answer?.relationship?.label || '').length > 0;

    render = (question: IRelationshipQuestion, wizard: MiniWizardViewModel) => {
        return <RelationshipQuestionView question={question} wizard={wizard} />;
    };
}

export const RelationshipQuestionView: FC<IQuestionViewProps<IRelationshipQuestion>> = ({ question, wizard }) => {
    const dataProvider = useInstance(SupportGroupDataProvider);
    const labelProvider = useInstance(LabelProvider);

    const fieldRef = useRef<InternalSelect<ISelected>>(null);

    useEffect(() => {
        if (isPlatform('desktop') || isPlatform('capacitor')) {
            const timeout = setTimeout(() => {
                fieldRef.current?.focus();
            }, 350); // need to wait 350ms for step animation to complete
            return () => clearTimeout(timeout);
        }
    }, []);

    const onRelationshipSelected = (relationship?: ISelected) => {
        wizard.answer({
            primaryCaregiver: question.answer?.primaryCaregiver,
            relationship: {
                value: relationship?.value || '',
                label: relationship?.label || ''
            }
        });
    };

    const togglePrimaryCaregiver = (checked: boolean) => {
        wizard.answer({
            relationship: question.answer?.relationship,
            primaryCaregiver: checked
        });
    };

    return (
        <Step
            question={question.questionTitle}
            subTitle={question.subTitle}
        >
            <SearchableSelectField
                innerRef={fieldRef}
                data-type="recipient-dropdown"
                onChange={onRelationshipSelected}
                options={dataProvider.getRelationshipSelectOptions()}
                placeholder="Search or select relationship"
                label={labelProvider.getLabel('CARE_RECIPIENT_RELATIONSHIP_TO_CARE_MAP_CREATOR', {careRecipient: question.recipientFirstName})}
                selected={question.answer?.relationship}
            />
            <div style={{width: '80%', marginTop: 24}}>
                <CaregiverToggle
                    checked={question.answer?.primaryCaregiver}
                    onToggle={togglePrimaryCaregiver}
                />
                <div style={{marginTop: 8}}>
                    <Text>
                        <span style={{color: 'var(--ion-color-step-700)'}}>
                            Innerhive assumes you are a caregiver for {question.recipientFirstName}, but if you are not, you can turn this off and add other's info later!
                        </span>
                    </Text>
                </div>
            </div>
        </Step>

    );
};

